import React from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Loader2, Camera, X, CheckCircle2, ArrowLeft, Sparkles } from 'lucide-react';
import { demandeDevisApi } from '../../lib/api';
import { AddressAutocompleteInput } from './AddressAutocompleteInput';
import { annoncerAuSiteParent } from '../../lib/annonceSiteParent';
import { ChampsPro, CHAMPS_PRO_PAR_DEFAUT, lireChampsPro } from '../../lib/champsDemandeDevisPro';

/**
 * DEMANDE DE DEVIS DEPUIS LE SIMULATEUR (2026-09-20, demande d'Emeni pour Shampouine le !).
 *
 * Le simulateur du site estime, puis propose « Faire ma demande de devis » : il ouvre cette page dans une
 * fenêtre par-dessus le site de l'établissement (le visiteur ne quitte jamais la page). Deux écrans, dans
 * cet ordre — d'abord les photos, ensuite les coordonnées : on demande l'effort avant l'identité, jamais
 * l'inverse, sinon le visiteur remplit un formulaire sans savoir à quoi il s'engage.
 *
 * Le prix affiché reste une ESTIMATION : seul l'établissement arrête le tarif réel, après avoir regardé
 * les photos. Cette page le dit à chaque écran, sans le noyer.
 */

interface Ligne { id: string; qty: number; label: string; unit_type: string; kind: string; unit_price_ttc: number }
interface Formule { id: string; name: string; occurrences_total: number; validity_months: number }
interface Contexte {
    company_name: string;
    brand_color: string;
    logo_url: string;
    audience: 'particulier' | 'professionnel';
    lignes: Ligne[];
    total_ttc: number;
    formule: Formule | null;
    // Ce que l'etablissement fait des photos : 'aucune' (l'etape disparait), 'facultatif' (defaut),
    // 'obligatoire' (au moins une, sinon la demande n'est pas acceptee).
    photos_mode: 'aucune' | 'facultatif' | 'obligatoire';
    // Informations demandées à un professionnel (réglage de l'établissement).
    champs_pro?: ChampsPro;
}

const eur = (n: number) => `${(Math.round(n * 100) / 100).toLocaleString('fr-FR', { minimumFractionDigits: n % 1 ? 2 : 0, maximumFractionDigits: 2 })} €`;
const uniteLabel = (u: string) => (u === 'm2' ? 'm²' : u === 'heure' ? 'h' : u === 'ml' ? 'ml' : '×');

// Trois photos par prestation au plus : au-delà, le visiteur envoie des doublons et la demande devient
// lourde à charger pour l'établissement qui l'ouvre depuis son téléphone.
const MAX_PHOTOS_PAR_LIGNE = 3;

export function DemandeDevisPage() {
    const { clientId } = useParams<{ clientId: string }>();
    const [params] = useSearchParams();
    const estimationToken = params.get('estimation') || '';
    // Le widget ajoute `fige=1` quand la balise du site vise explicitement un public (data-audience) :
    // le visiteur n'a alors jamais eu de choix à faire, et ne doit pas s'en voir proposer un ici.
    const publicFige = params.get('fige') === '1';

    const [ctx, setCtx] = React.useState<Contexte | null>(null);
    const [phase, setPhase] = React.useState<'chargement' | 'photos' | 'coordonnees' | 'envoye' | 'introuvable'>('chargement');
    const [erreur, setErreur] = React.useState('');
    const [envoi, setEnvoi] = React.useState(false);

    // Photos rangées par prestation : c'est ce découpage qui permet à l'établissement de savoir, dans le
    // dossier, quelle photo illustre quelle ligne — donc de rechiffrer ligne par ligne.
    const [photos, setPhotos] = React.useState<Record<string, { name: string; data: string }[]>>({});

    const [type, setType] = React.useState<'PRIVÉ' | 'PROFESSIONNEL'>('PRIVÉ');
    const [firstName, setFirstName] = React.useState('');
    const [lastName, setLastName] = React.useState('');
    const [companyName, setCompanyName] = React.useState('');
    const [email, setEmail] = React.useState('');
    const [phone, setPhone] = React.useState('');
    const [address, setAddress] = React.useState('');
    const [postalCode, setPostalCode] = React.useState('');
    const [city, setCity] = React.useState('');
    const [etablissementType, setEtablissementType] = React.useState('');
    const [contactFonction, setContactFonction] = React.useState('');
    const [siret, setSiret] = React.useState('');
    const [message, setMessage] = React.useState('');
    const [formuleRetenue, setFormuleRetenue] = React.useState(true);

    // Estimation des frais kilométriques affichée en direct pendant la saisie de l'adresse (2026-09-25,
    // signalé par Emeni : le visiteur n'était jamais prévenu qu'ils s'appliquaient). Purement informatif :
    // le montant réel n'est fixé que plus tard, par l'établissement, sur le devis définitif.
    const [kmFee, setKmFee] = React.useState<{ fee: number; distance_km: number; waiver_threshold_ttc: number; aller_retour?: boolean } | null>(null);
    React.useEffect(() => {
        if (!clientId || !address.trim()) { setKmFee(null); return; }
        const timer = setTimeout(() => {
            demandeDevisApi.getKmFeeEstimate(clientId, address.trim(), postalCode.trim(), city.trim(), ctx?.audience || 'particulier')
                .then((data: any) => setKmFee(data?.fee > 0 ? data : null))
                .catch(() => setKmFee(null));
        }, 600);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientId, address, postalCode, city, ctx?.audience]);

    // Thème clair imposé, comme les autres pages publiques (2026-09-21) : cette page s'affiche dans une
    // fenêtre posée sur le site de l'établissement, mais elle est servie par NOTRE domaine — elle héritait
    // donc du thème sombre choisi par l'établissement dans son espace, et le visiteur voyait un formulaire
    // noir au milieu d'un site clair. Le thème d'origine est remis en quittant la page.
    React.useEffect(() => {
        const root = document.documentElement;
        const themePrecedent = root.getAttribute('data-theme');
        const etaitSombre = root.classList.contains('dark');
        root.setAttribute('data-theme', 'light');
        root.classList.remove('dark');
        root.classList.add('light');
        return () => {
            if (themePrecedent) root.setAttribute('data-theme', themePrecedent); else root.removeAttribute('data-theme');
            root.classList.toggle('dark', etaitSombre);
            root.classList.toggle('light', !etaitSombre);
        };
    }, []);

    React.useEffect(() => {
        if (!clientId || !estimationToken) { setPhase('introuvable'); return; }
        demandeDevisApi.getContexte(clientId, estimationToken).then((data: any) => {
            if (!data || data.error) { setPhase('introuvable'); return; }
            setCtx(data);
            setType(data.audience === 'professionnel' ? 'PROFESSIONNEL' : 'PRIVÉ');
            // L'etablissement qui ne veut pas de photos envoie son visiteur droit aux coordonnees :
            // un ecran vide « joignez des photos » sans zone de depot n'aurait aucun sens.
            setPhase(data.photos_mode === 'aucune' ? 'coordonnees' : 'photos');

            const brandColor = data.brand_color || '#987b55';
            document.documentElement.style.setProperty('--brand-color', brandColor);
            const hex = brandColor.replace('#', '');
            const r = parseInt(hex.substring(0, 2), 16) || 0;
            const g = parseInt(hex.substring(2, 4), 16) || 0;
            const b = parseInt(hex.substring(4, 6), 16) || 0;
            document.documentElement.style.setProperty('--brand-fg', (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.5 ? '#000000' : '#ffffff');
        }).catch(() => setPhase('introuvable'));
    }, [clientId, estimationToken]);

    const ajouterPhotos = (ligneId: string, e: React.ChangeEvent<HTMLInputElement>) => {
        const dejaLa = (photos[ligneId] || []).length;
        const fichiers = Array.from(e.target.files || []).slice(0, MAX_PHOTOS_PAR_LIGNE - dejaLa);
        e.target.value = '';
        fichiers.forEach((file) => {
            const reader = new FileReader();
            reader.onload = () => {
                setPhotos((prev) => {
                    const actuelles = prev[ligneId] || [];
                    if (actuelles.length >= MAX_PHOTOS_PAR_LIGNE) return prev;
                    return { ...prev, [ligneId]: [...actuelles, { name: file.name, data: reader.result as string }] };
                });
            };
            reader.readAsDataURL(file);
        });
    };

    const retirerPhoto = (ligneId: string, index: number) => {
        setPhotos((prev) => ({ ...prev, [ligneId]: (prev[ligneId] || []).filter((_, i) => i !== index) }));
    };

    const nombrePhotos = Object.values(photos).reduce((a, l) => a + l.length, 0);

    const envoyer = async () => {
        if (!clientId) return;
        setErreur('');
        setEnvoi(true);
        try {
            const aPlat = Object.entries(photos).flatMap(([itemId, liste]) => liste.map((p) => ({ item_id: itemId, name: p.name, data: p.data })));
            const r: any = await demandeDevisApi.envoyer(clientId, {
                estimation_token: estimationToken,
                type,
                first_name: firstName,
                last_name: lastName,
                company_name: type === 'PROFESSIONNEL' ? companyName : '',
                email,
                phone,
                address,
                postal_code: postalCode,
                city,
                establishment_type: type === 'PROFESSIONNEL' ? etablissementType : '',
                contact_role: type === 'PROFESSIONNEL' ? contactFonction : '',
                siret: type === 'PROFESSIONNEL' ? siret : '',
                message,
                garder_formule: formuleRetenue,
                photos: aPlat,
            });
            if (r?.error) { setErreur(r.error); setEnvoi(false); return; }
            setPhase('envoye');
            // Le site de l'etablissement peut alors compter sa conversion (Google Ads, Analytics) : il
            // n'avait jusqu'ici aucun moyen de savoir ce qui se passait dans l'iframe.
            annoncerAuSiteParent('devis-envoye', { parcours: 'simulateur', reference: r?.id || null });
        } catch (e: any) {
            setErreur(e?.message || "L'envoi n'a pas abouti. Réessayez dans un instant.");
            setEnvoi(false);
        }
    };

    if (phase === 'chargement') {
        return <div className="min-h-screen flex items-center justify-center bg-surface-soft"><Loader2 className="animate-spin text-content-muted" /></div>;
    }

    if (phase === 'introuvable' || !ctx) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-surface-soft p-6">
                <div className="max-w-md text-center space-y-2">
                    <p className="text-lg font-bold text-content">Cette demande n'est plus valable</p>
                    <p className="text-sm text-content-muted">Refaites votre simulation depuis le site : le récapitulatif sera de nouveau disponible.</p>
                </div>
            </div>
        );
    }

    if (phase === 'envoye') {
        return (
            <div className="min-h-screen flex items-center justify-center bg-surface-soft p-6">
                <div className="max-w-md w-full bg-surface border border-border rounded-2xl p-8 text-center space-y-3">
                    <CheckCircle2 size={44} className="mx-auto text-green-500" />
                    <h1 className="text-xl font-black text-content">Votre demande est partie</h1>
                    <p className="text-sm text-content-muted">
                        {ctx.company_name} étudie votre demande{nombrePhotos > 0 ? ' et vos photos' : ''}, puis vous envoie le devis définitif par email
                        à <b className="text-content">{email}</b>.
                    </p>
                    <p className="text-xs text-content-muted">Le montant que vous avez vu n'était qu'une estimation : seul le devis fait foi.</p>
                    {/* Le logo signe la confirmation : le visiteur vient de confier ses coordonnées, il
                        doit voir à qui. Absent du compte, on n'affiche rien plutôt qu'un cadre vide. */}
                    {ctx.logo_url && <img src={ctx.logo_url} alt={ctx.company_name} className="mx-auto max-h-14 max-w-[190px] object-contain pt-1" />}
                </div>
            </div>
        );
    }

    // Un professionnel ne voit que les informations que l'établissement a cochées ; un particulier garde le
    // formulaire habituel. Les champs obligatoires bloquent le bouton d'envoi (le serveur les rejoue).
    const estPro = type === 'PROFESSIONNEL';
    const champsPro = lireChampsPro(ctx?.champs_pro || CHAMPS_PRO_PAR_DEFAUT);
    const veut = (k: keyof ChampsPro) => !estPro || champsPro[k].demande;
    const exige = (k: keyof ChampsPro) => estPro && champsPro[k].demande && champsPro[k].obligatoire;
    const etoile = (k: keyof ChampsPro) => (exige(k) ? ' *' : '');
    const infoManquante =
        !email.trim()
        || (estPro ? (exige('company_name') && !companyName.trim()) : !(firstName.trim() || lastName.trim()))
        || (estPro ? (exige('phone') && !phone.trim()) : !phone.trim())
        || (exige('type_etablissement') && !etablissementType.trim())
        || (exige('address') && !address.trim())
        || (exige('contact_nom') && !(firstName.trim() || lastName.trim()))
        || (exige('contact_fonction') && !contactFonction.trim())
        || (exige('siret') && !siret.trim());

    const champ = 'w-full bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500';

    return (
        <div className="min-h-screen bg-surface-soft py-6 px-4">
            <div className="max-w-2xl mx-auto space-y-4">
                {/* Ce que le visiteur a simulé — visible en permanence : c'est ce qui donne du sens aux
                    photos qu'on lui demande, et au prix qu'il a vu. */}
                <div className="bg-surface border border-border rounded-2xl p-5">
                    <div className="flex items-start justify-between gap-4">
                        <div>
                            <p className="text-[11px] font-bold text-content-muted uppercase tracking-widest">Votre demande de devis</p>
                            <h1 className="text-lg font-black text-content">{ctx.company_name}</h1>
                        </div>
                        {ctx.logo_url && <img src={ctx.logo_url} alt="" className="h-9 object-contain" />}
                    </div>
                    <div className="mt-4 space-y-1.5">
                        {ctx.lignes.map((l) => (
                            <div key={l.id} className="flex items-center justify-between gap-3 text-sm">
                                <span className="text-content">{l.label}</span>
                                <span className="text-content-muted tabular-nums shrink-0">
                                    {l.qty} {uniteLabel(l.unit_type)} · {eur(l.qty * l.unit_price_ttc)}
                                </span>
                            </div>
                        ))}
                    </div>
                    <div className="mt-3 pt-3 border-t border-border flex items-center justify-between">
                        <span className="text-sm font-bold text-content">Estimation indicative</span>
                        <span className="text-lg font-black text-content tabular-nums">{eur(ctx.total_ttc)} TTC</span>
                    </div>
                    <p className="mt-2 text-[11px] text-content-muted">
                        Ce montant n'engage personne : {ctx.company_name} établit le devis définitif après avoir étudié votre demande.
                    </p>
                </div>

                {phase === 'photos' && (
                    <div className="bg-surface border border-border rounded-2xl p-5 space-y-4">
                        <div>
                            <h2 className="text-base font-black text-content">Des photos de ce qu'il faut traiter</h2>
                            <p className="text-sm text-content-muted mt-1">
                                {ctx.photos_mode === 'obligatoire'
                                    ? `${ctx.company_name} établit ses devis sur photos : il en faut au moins une pour envoyer votre demande.`
                                    : "Facultatives — mais c'est ce qui permet d'établir un devis juste du premier coup, sans visite préalable."}
                            </p>
                            <ul className="mt-2 text-[11px] text-content-muted space-y-0.5 pl-4 list-disc">
                                <li><b className="text-content">L'objet en entier</b>, avec un peu de marge autour.</li>
                                <li><b className="text-content">En lumière naturelle</b>, sans flash — il fait briller les taches.</li>
                                <li><b className="text-content">La tache ou l'usure bien visible</b>, en gros plan si besoin.</li>
                            </ul>
                        </div>

                        {ctx.lignes.filter((l) => l.kind !== 'option').map((ligne) => {
                            const liste = photos[ligne.id] || [];
                            return (
                                <div key={ligne.id} className="p-3 rounded-xl bg-surface-soft border border-border space-y-2">
                                    <p className="text-[11px] font-bold text-content-muted uppercase tracking-widest flex items-center gap-1.5">
                                        <Camera size={13} /> {ligne.label}
                                    </p>
                                    <div className="grid grid-cols-3 gap-2">
                                        {liste.map((f, i) => (
                                            <div key={i} className="relative aspect-[4/3] rounded-lg overflow-hidden border border-border bg-surface">
                                                <img src={f.data} alt={f.name} className="w-full h-full object-cover" />
                                                <button type="button" onClick={() => retirerPhoto(ligne.id, i)}
                                                    className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center" aria-label="Retirer">
                                                    <X size={12} />
                                                </button>
                                            </div>
                                        ))}
                                        {liste.length < MAX_PHOTOS_PAR_LIGNE && (
                                            <label className="aspect-[4/3] rounded-lg border-2 border-dashed border-border bg-surface flex flex-col items-center justify-center gap-1 text-[11px] text-content-muted hover:text-content hover:border-brand-500 cursor-pointer transition-colors">
                                                <Camera size={18} />
                                                <span>{liste.length === 0 ? 'Prendre ou choisir' : 'Une autre'}</span>
                                                <input type="file" accept="image/*" capture="environment" multiple className="hidden"
                                                    onChange={(e) => ajouterPhotos(ligne.id, e)} />
                                            </label>
                                        )}
                                    </div>
                                </div>
                            );
                        })}

                        <button onClick={() => setPhase('coordonnees')}
                            disabled={ctx.photos_mode === 'obligatoire' && nombrePhotos === 0}
                            className="w-full px-4 py-3 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg text-sm font-bold disabled:opacity-50 transition-all">
                            {nombrePhotos > 0
                                ? `Continuer avec ${nombrePhotos} photo${nombrePhotos > 1 ? 's' : ''}`
                                : ctx.photos_mode === 'obligatoire' ? 'Ajoutez au moins une photo' : 'Continuer sans photo'}
                        </button>
                    </div>
                )}

                {phase === 'coordonnees' && (
                    <div className="bg-surface border border-border rounded-2xl p-5 space-y-4">
                        {ctx.photos_mode !== 'aucune' && (
                            <button onClick={() => setPhase('photos')} className="text-xs font-bold text-content-muted hover:text-content flex items-center gap-1">
                                <ArrowLeft size={13} /> Revenir aux photos
                            </button>
                        )}
                        <div>
                            <h2 className="text-base font-black text-content">Vos coordonnées</h2>
                            <p className="text-sm text-content-muted mt-1">C'est par email que le devis vous parviendra.</p>
                        </div>

                        {/* Le public n'est reproposé QUE si le simulateur laissait le choix au visiteur
                            (2026-09-21, Emeni) : quand la balise du site est posée pour les particuliers, lui
                            remettre l'onglet « Professionnel » sous les yeux n'a aucun sens — le parcours, les
                            tarifs et les formules qu'il vient de voir étaient ceux des particuliers. */}
                        {!publicFige && (
                            <div className="flex gap-2">
                                {(['PRIVÉ', 'PROFESSIONNEL'] as const).map((t) => (
                                    <button key={t} type="button" onClick={() => setType(t)}
                                        className={`flex-1 px-3 py-2 rounded-xl text-sm font-bold border transition-colors ${type === t ? 'bg-brand-600 text-brand-fg border-brand-600' : 'bg-surface-soft text-content-muted border-border hover:text-content'}`}>
                                        {t === 'PRIVÉ' ? 'Particulier' : 'Professionnel'}
                                    </button>
                                ))}
                            </div>
                        )}

                        {estPro && (
                            <input className={champ} placeholder="Nom de l'établissement *" value={companyName} onChange={(e) => setCompanyName(e.target.value)} />
                        )}
                        {estPro && veut('type_etablissement') && (
                            <>
                                <input className={champ} list="types-etablissement" placeholder={`Type d'établissement${etoile('type_etablissement')} (restaurant, hôtel…)`}
                                    value={etablissementType} onChange={(e) => setEtablissementType(e.target.value)} />
                                <datalist id="types-etablissement">
                                    {['Restaurant', 'Hôtel', 'Bar', 'Café', 'Brasserie', 'Traiteur', 'Boulangerie', 'Boutique', 'Bureau', 'Autre'].map((t) => <option key={t} value={t} />)}
                                </datalist>
                            </>
                        )}
                        {veut('contact_nom') && (
                            <div className="grid grid-cols-2 gap-2">
                                <input className={champ} placeholder="Prénom" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                                <input className={champ} placeholder={estPro ? `Nom du contact${etoile('contact_nom')}` : 'Nom *'} value={lastName} onChange={(e) => setLastName(e.target.value)} />
                            </div>
                        )}
                        {estPro && veut('contact_fonction') && (
                            <input className={champ} placeholder={`Fonction du contact${etoile('contact_fonction')}`} value={contactFonction} onChange={(e) => setContactFonction(e.target.value)} />
                        )}
                        <input className={champ} type="email" placeholder="Email *" value={email} onChange={(e) => setEmail(e.target.value)} />
                        {veut('phone') && (
                            <input className={champ} type="tel" placeholder={estPro ? `Téléphone${etoile('phone')}` : 'Téléphone *'} value={phone} onChange={(e) => setPhone(e.target.value)} />
                        )}
                        {estPro && veut('siret') && (
                            <input className={champ} inputMode="numeric" placeholder={`Numéro de SIRET${etoile('siret')}`} value={siret} onChange={(e) => setSiret(e.target.value)} />
                        )}

                        {veut('address') && (
                            <AddressAutocompleteInput
                                value={address}
                                onChange={(v) => setAddress(v)}
                                onSelect={(choix) => { setAddress(choix.address); setPostalCode(choix.postcode); setCity(choix.city); }}
                                placeholder={`${estPro ? "Adresse de l'établissement" : "Adresse de l'intervention"}${etoile('address')}`}
                                className={champ}
                            />
                        )}
                        {kmFee && (
                            <p className="text-xs text-neutral-500 -mt-1">
                                Frais kilométriques estimés à cette adresse : environ {eur(kmFee.fee)}.
                                {kmFee.waiver_threshold_ttc > 0 && ` Offerts à partir de ${eur(kmFee.waiver_threshold_ttc)} de devis.`}
                                {' '}Montant indicatif, confirmé sur le devis définitif.
                            </p>
                        )}
                        {veut('address') && (
                            <div className="grid grid-cols-2 gap-2">
                                <input className={champ} placeholder="Code postal" value={postalCode} onChange={(e) => setPostalCode(e.target.value)} />
                                <input className={champ} placeholder="Ville" value={city} onChange={(e) => setCity(e.target.value)} />
                            </div>
                        )}

                        <textarea className={`${champ} resize-none`} rows={3} placeholder="Une précision utile ? (facultatif)"
                            value={message} onChange={(e) => setMessage(e.target.value)} />

                        {ctx.formule && (
                            <label className="flex items-start gap-2.5 p-3 rounded-xl bg-surface-soft border border-border cursor-pointer">
                                <input type="checkbox" checked={formuleRetenue} onChange={(e) => setFormuleRetenue(e.target.checked)} className="mt-0.5" />
                                <span className="text-sm text-content">
                                    <span className="font-bold flex items-center gap-1.5"><Sparkles size={13} /> Formule {ctx.formule.name}</span>
                                    <span className="block text-xs text-content-muted mt-0.5">
                                        {ctx.formule.occurrences_total} passages sur {ctx.formule.validity_months} mois — établissez-moi le devis sur cette base.
                                    </span>
                                </span>
                            </label>
                        )}

                        {erreur && <p className="text-sm text-red-500 font-semibold">{erreur}</p>}

                        <button onClick={envoyer} disabled={envoi || infoManquante}
                            className="w-full px-4 py-3 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg text-sm font-bold disabled:opacity-50 transition-all">
                            {envoi ? 'Envoi…' : 'Envoyer ma demande de devis'}
                        </button>
                        <p className="text-[11px] text-content-muted text-center">
                            Vos informations servent uniquement à établir ce devis. Rien n'est engagé tant que vous n'avez pas signé.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}
